import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getExplicitLanguageFromPathname } from "../lib/language.js";
import { hasLocalizedCounterpart } from "../content/pages/registry.js";
import { toShortCode } from "./useLanguage.js";

/** Short code to the resource key i18next was initialised with. */
const RESOURCE_KEY = { en: "enUS", el: "elGR" };

/**
 * The language a pathname is authoritatively served in, or `null` when it does
 * not decide.
 *
 * The same rule the i18next detector applies on first load, expressed once so
 * the two cannot drift: a first paint and a client-side navigation to the same
 * URL must not end up in different languages.
 *
 *   /el/terms          → "el"   the prefix says so
 *   /terms             → "en"   it has a Greek URL, so this one is English
 *   /guides            → null   no Greek URL; the stored choice still decides
 */
export const routeLanguage = (pathname) =>
  getExplicitLanguageFromPathname(pathname) ??
  (hasLocalizedCounterpart(pathname) ? "en" : null);

/**
 * Keep i18next in step with the language the URL declares.
 *
 * Mounted once, at the top of the app, because both directions matter and only
 * one of them lives under `/el`:
 *
 *   - arriving at `/el/terms` shows Greek whatever the stored preference says;
 *   - LEAVING `/el/` for `/terms` shows English again.
 *
 * The second is the half that was missing. `useRouteLanguage` used to run only
 * inside the Greek subtree, so a client-side navigation out of it left i18next
 * on Greek — and the English page rendered in Greek at its English URL, which
 * is precisely what the localized URLs exist to stop. A crawler never saw it,
 * because a crawler never navigates; a visitor who clicked "Guides" from the
 * Greek homepage saw it every time.
 *
 * A pathname that does not decide (a page with no localized URL) is left alone.
 * See `hasLocalizedCounterpart` for why that is the right shape for the
 * exception, and why it disappears by itself.
 */
export default function useRouteLanguage() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  useEffect(() => {
    const target = routeLanguage(pathname);
    if (!target) return;

    const current = toShortCode(i18n.resolvedLanguage || i18n.language);
    if (target === current) return;

    const key = RESOURCE_KEY[target];
    if (key) i18n.changeLanguage(key);
  }, [i18n, pathname]);
}
